import React from "react";
import { Box, Typography, Stack } from "@mui/material";
import { motion } from "framer-motion";

/**
 * Compact KPI card with an icon, value and label.
 * Shared across dashboards (teacher + admin).
 *
 * @param {React.ElementType} icon - MUI icon component
 * @param {React.ReactNode} value - Primary metric value
 * @param {string} label - Uppercase descriptor under the value
 * @param {string} color - Accent color for the icon chip
 * @param {number} delay - Entrance animation delay (seconds)
 */
const KPICard = ({ icon: Icon, value, label, color = "var(--text-primary)", delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
    style={{ flex: 1, minWidth: 0 }}
  >
    <Box sx={{
      p: 2.5, borderRadius: "14px",
      background: "color-mix(in srgb, var(--text-primary) 2.5%, transparent)",
      border: "1px solid color-mix(in srgb, var(--text-primary) 5%, transparent)",
      height: "100%",
    }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{
          width: 38, height: 38, borderRadius: "10px",
          display: "flex", alignItems: "center", justifyContent: "center",
          background: `color-mix(in srgb, ${color} 10%, transparent)`,
        }}>
          {Icon && <Icon sx={{ fontSize: 18, color }} />}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{
            fontSize: "1.25rem", fontWeight: 700,
            color: "var(--text-primary)",
            fontFamily: "'Space Grotesk', 'Inter', sans-serif",
            lineHeight: 1.1,
          }}>
            {value}
          </Typography>
          <Typography sx={{
            fontSize: "0.68rem", fontWeight: 500,
            color: "color-mix(in srgb, var(--text-primary) 40%, transparent)",
            fontFamily: "'Inter', sans-serif",
            textTransform: "uppercase", letterSpacing: "0.06em", mt: 0.2,
          }}>
            {label}
          </Typography>
        </Box>
      </Stack>
    </Box>
  </motion.div>
);

export default KPICard;
